// =============================================================================
// src/features/crisis/crisis-action-plan.ts
// Plano de contingencia interativo (human-in-the-loop): aprovar/ajustar/descartar.
//
// Proveniencia: porte FIEL de assets/crisis.js 327-408 (renderControls/
//   updateCount/setState/openAdjust + delegacao de clique em #crzPlanList).
//   Cada acao alterna entre pending/approved/dismissed; o ajuste abre um textarea
//   inline; o contador "N de M aprovadas" reflete a contagem de .is-approved.
//
// SRP: so o ciclo de vida das acoes do plano. Os icones inline (TI_*) sao os
//   mesmos do legado, mantidos como constantes greppaveis.
// =============================================================================

import { byId, on, qs, qsa } from '../../app/dom';
import { getCrisisLiveActions } from './crisis-live-store';

/** Estado de uma acao do plano (assets/crisis.js 339). */
type CrisisActionState = 'pending' | 'approved' | 'dismissed';

/** Icones Tabler inline usados nos controles (assets/crisis.js 328-335). */
const TI = {
  check: '<i class="ti ti-check crz-ico sm" aria-hidden="true"></i>',
  refresh: '<i class="ti ti-refresh crz-ico sm" aria-hidden="true"></i>',
  pencil: '<i class="ti ti-pencil crz-ico sm" aria-hidden="true"></i>',
  x: '<i class="ti ti-x crz-ico sm" aria-hidden="true"></i>',
} as const;

/**
 * Liga o plano de contingencia. Inicializa cada acao em 'pending', renderiza os
 * controles, atualiza o contador e delega os cliques (assets/crisis.js 384-408).
 * No-op se #crzPlanList faltar.
 */
export function bindCrisisActionPlan(takeover: HTMLElement): void {
  const planList = qs<HTMLElement>('#crzPlanList', takeover);
  if (!planList) return;
  hydratePlanFromLiveActions(planList);
  const plan = new CrisisActionPlan(takeover);
  on(planList, 'click', (e) => plan.handleClick(e));
  qsa<HTMLElement>('#crzPlanList .crz-action', takeover).forEach((action) => {
    action.dataset.state = 'pending';
    renderActionControls(action);
  });
  plan.refreshCount();
}

function hydratePlanFromLiveActions(planList: HTMLElement): void {
  const actions = getCrisisLiveActions();
  if (!actions.length) return;
  planList.innerHTML = actions.map((action, index) => liveActionHtml(action, index)).join('');
}

function liveActionHtml(action: ReturnType<typeof getCrisisLiveActions>[number], index: number): string {
  const tags = action.tags.map((tag) => `<span class="crz-chip">${escapeHtml(tag)}</span>`).join('');
  return (
    `<div class="crz-action" data-id="live-${index + 1}">` +
    `<div class="crz-action-status"><span class="crz-a-dot"></span></div>` +
    `<div class="crz-action-body">` +
    `<p class="crz-action-text">${escapeHtml(action.text)}</p>` +
    `<div class="crz-action-meta">` +
    `<span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> ${escapeHtml(action.when)}</span>` +
    tags +
    `</div></div>` +
    `<div class="crz-action-controls"></div>` +
    `</div>`
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Estado mutavel do plano: contagem e transicoes de acao. */
class CrisisActionPlan {
  constructor(private readonly takeover: HTMLElement) {}

  /** Despacha o clique para a transicao correspondente (assets/crisis.js 385-403). */
  handleClick(e: MouseEvent): void {
    const btn = (e.target as Element | null)?.closest<HTMLElement>('button[data-act]');
    if (!btn) return;
    const action = btn.closest<HTMLElement>('.crz-action');
    if (!action) return;
    /* v8 ignore next -- btn casou button[data-act], dataset.act sempre definido; fallback defensivo CLAUDE.md rule 11 */
    this.applyAction(action, btn.dataset.act ?? '');
  }

  /** Aplica a acao do botao a um cartao do plano (assets/crisis.js 390-402). */
  private applyAction(action: HTMLElement, act: string): void {
    if (act === 'approve') this.setState(action, 'approved');
    else if (act === 'dismiss') this.setState(action, 'dismissed');
    else if (act === 'undo' || act === 'restore') this.setState(action, 'pending');
    else if (act === 'adjust') openAdjustBox(action);
    else if (act === 'save-adjust') this.saveAdjust(action);
    else if (act === 'cancel-adjust') removeAdjustBox(action);
  }

  /** Aplica estado + marca o dot + re-renderiza controles + recalcula (assets/crisis.js 360-368). */
  private setState(action: HTMLElement, state: CrisisActionState): void {
    action.dataset.state = state;
    action.classList.toggle('is-approved', state === 'approved');
    action.classList.toggle('is-dismissed', state === 'dismissed');
    const dot = qs<HTMLElement>('.crz-a-dot', action);
    if (dot) dot.innerHTML = state === 'approved' ? CHECK_DOT_HTML : '';
    renderActionControls(action);
    this.refreshCount();
  }

  /** Salva o texto ajustado e fecha a caixa (assets/crisis.js 394-399). */
  private saveAdjust(action: HTMLElement): void {
    const box = qs<HTMLElement>('.crz-adjust-box', action);
    if (!box) return;
    const value = qs<HTMLTextAreaElement>('.crz-adjust-input', box)?.value.trim() ?? '';
    const text = qs<HTMLElement>('.crz-action-text', action);
    if (value && text) text.textContent = value;
    action.classList.add('is-adjusted');
    box.remove();
  }

  /** Atualiza "N de M aprovadas" (assets/crisis.js 353-358). */
  refreshCount(): void {
    const total = qsa('#crzPlanList .crz-action', this.takeover).length;
    const approved = qsa('#crzPlanList .crz-action.is-approved', this.takeover).length;
    byId('crzApprovedN').textContent = String(approved);
    byId('crzTotalN').textContent = String(total);
  }
}

/** Marca de check no dot de uma acao aprovada (assets/crisis.js 365). */
const CHECK_DOT_HTML = '<i class="ti ti-check" style="font-size:11px" aria-hidden="true"></i>';

/** Renderiza os controles conforme o estado da acao (assets/crisis.js 337-351). */
function renderActionControls(action: HTMLElement): void {
  const ctl = qs<HTMLElement>('.crz-action-controls', action);
  if (!ctl) return;
  /* v8 ignore next -- bindCrisisActionPlan inicializa data-state='pending'; fallback ?? 'pending' inalcançável */
  const state = action.dataset.state ?? 'pending';
  if (state === 'approved') ctl.innerHTML = controlsApproved();
  else if (state === 'dismissed') ctl.innerHTML = controlsDismissed();
  else ctl.innerHTML = controlsPending();
}

/** Controles do estado aprovado (assets/crisis.js 340-342). */
function controlsApproved(): string {
  return (
    `<span class="crz-status-tag running">${TI.check} Em execução</span>` +
    `<button class="crz-btn crz-btn-ghost" data-act="undo">Desfazer</button>`
  );
}

/** Controles do estado descartado (assets/crisis.js 343-345). */
function controlsDismissed(): string {
  return (
    `<span class="crz-status-tag dropped">Descartada</span>` +
    `<button class="crz-btn crz-btn-ghost" data-act="restore">${TI.refresh} Restaurar</button>`
  );
}

/** Controles do estado pendente (assets/crisis.js 347-349). */
function controlsPending(): string {
  return (
    `<button class="crz-btn crz-btn-approve" data-act="approve">${TI.check} Aprovar</button>` +
    `<button class="crz-btn crz-btn-ghost" data-act="adjust" aria-label="Ajustar">${TI.pencil}</button>` +
    `<button class="crz-btn crz-btn-ghost icon-only" data-act="dismiss" aria-label="Descartar">${TI.x}</button>`
  );
}

/** Abre o textarea de ajuste pre-preenchido com o texto atual (assets/crisis.js 370-382). */
function openAdjustBox(action: HTMLElement): void {
  if (qs('.crz-adjust-box', action)) return;
  const body = qs<HTMLElement>('.crz-action-body', action);
  const current = qs<HTMLElement>('.crz-action-text', action)?.textContent?.trim() ?? '';
  if (!body) return;
  const box = document.createElement('div');
  box.className = 'crz-adjust-box';
  box.innerHTML = ADJUST_BOX_HTML;
  body.appendChild(box);
  const textarea = qs<HTMLTextAreaElement>('.crz-adjust-input', box);
  /* v8 ignore next -- ADJUST_BOX_HTML sempre inclui .crz-adjust-input; guarda defensiva CLAUDE.md rule 11 */
  if (!textarea) return;
  textarea.value = current;
  textarea.focus();
}

/** Remove a caixa de ajuste se aberta (assets/crisis.js 400-401). */
function removeAdjustBox(action: HTMLElement): void {
  qs<HTMLElement>('.crz-adjust-box', action)?.remove();
}

/** Markup da caixa de ajuste (assets/crisis.js 376-378). */
const ADJUST_BOX_HTML =
  '<textarea class="crz-adjust-input" aria-label="Ajustar ação"></textarea>' +
  '<div class="crz-adjust-actions"><button class="crz-btn crz-btn-approve" data-act="save-adjust">Salvar ajuste</button>' +
  '<button class="crz-btn crz-btn-ghost" data-act="cancel-adjust">Cancelar</button></div>';
